import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Raw transport surface for the admin email-server feature (PTV2-1162): CRUD over SES servers, a
 * per-server test-send, a no-send connection check, and the template-variable palette. Hand-written
 * slice DTOs (snake_case) — the api-layer mapper normalises to camelCase view models. The stored
 * {@code secret_access_key} is write-only: sent on create/update, never returned; {@code has_secret}
 * tells the UI whether one is stored.
 */

/** Wire DTO for an email server. All fields optional on the wire; the mapper normalises. */
export interface EmailServerDto {
  id?: string;
  name?: string;
  region?: string;
  from_address?: string;
  from_name?: string | null;
  reply_to?: string | null;
  configuration_set?: string | null;
  access_key_id?: string | null;
  /** Write-only — never present on reads. */
  secret_access_key?: string | null;
  /** Read-only — whether a secret is stored. */
  has_secret?: boolean;
  is_default?: boolean;
  active?: boolean;
  sns_topic_arn_allowlist?: string | null;
  feedback_queue_url?: string | null;
  feedback_queue_region?: string | null;
}

export type EmailServerPageDto = PageEnvelope<EmailServerDto>;

export interface EmailTestRequestDto {
  to: string;
}

export interface EmailTestResultDto {
  ok: boolean;
  message_id?: string | null;
  error_code?: string | null;
  detail?: string | null;
}

export interface EmailServerStatusDto {
  ok: boolean;
  sending_enabled?: boolean | null;
  production_access?: boolean | null;
  max_24_hour_send?: number | null;
  error_code?: string | null;
  detail?: string | null;
  queue_configured?: boolean;
  queue_ok?: boolean | null;
  queue_error_code?: string | null;
  queue_detail?: string | null;
}

function encode(id: string): string {
  return encodeURIComponent(id);
}

export const adminEmailEndpoints = {
  /** GET /email-servers — paginated list for the current tenant. */
  getEmailServers: (params?: {
    page?: number;
    size?: number;
    active?: boolean;
    signal?: AbortSignal;
  }): Promise<EmailServerPageDto> => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.size !== undefined) search.set('size', String(params.size));
    if (params?.active !== undefined) search.set('active', String(params.active));
    const query = search.toString();
    const path = query.length > 0 ? `/email-servers?${query}` : '/email-servers';
    return http.get<EmailServerPageDto>(
      path,
      params?.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },

  /** POST /email-servers — create. 409 if the name is taken; 422 if the access-key creds are missing. */
  createEmailServer: (body: EmailServerDto, options?: { signal?: AbortSignal }): Promise<EmailServerDto> =>
    http.post<EmailServerDto>('/email-servers', body, options),

  /** PUT /email-servers/{id} — update. A blank secret keeps the stored one. */
  updateEmailServer: (
    id: string,
    body: EmailServerDto,
    options?: { signal?: AbortSignal },
  ): Promise<EmailServerDto> => http.put<EmailServerDto>(`/email-servers/${encode(id)}`, body, options),

  /** DELETE /email-servers/{id}. */
  deleteEmailServer: (id: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/email-servers/${encode(id)}`, options),

  /** POST /email-servers/{id}/test — send a test email; the outcome is in the result (never throws on send failure). */
  testEmailServer: (
    id: string,
    body: EmailTestRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<EmailTestResultDto> =>
    http.post<EmailTestResultDto>(`/email-servers/${encode(id)}/test`, body, options),

  /** GET /email-servers/{id}/status — SES GetAccount connection check (no send). */
  getEmailServerStatus: (id: string, options?: { signal?: AbortSignal }): Promise<EmailServerStatusDto> =>
    http.get<EmailServerStatusDto>(`/email-servers/${encode(id)}/status`, options),
};
